import type { IconName } from "@verbleif/lib/src/components/Icon/icons";
import type { RouteLocationRaw } from "vue-router";
import { ThemeSetting, useTheme } from "@verbleif/shared/src/composables/Theme/Theme";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { version as pkgVersion } from "../../../package.json";

export interface NavbarItem {
  key: string
  to: RouteLocationRaw
  icon: IconName
  label: string
}

const version = import.meta.env.VITE_APP_VERSION ?? (import.meta.env.DEV ? "dev" : pkgVersion);

/** State and actions shared by the desktop navbar and the phone tab bar. */
export function useNavbar() {
  const { t } = useI18n();
  const { themeSetting, theme } = useTheme();

  const items = computed<NavbarItem[]>(() => [
    { key: "dashboards", to: { name: "dashboards" }, icon: "chart-line", label: t("cms.dashboards.title") },
    { key: "clients", to: { name: "client-overview" }, icon: "building-complex", label: t("cms.navbar.customers") },
    { key: "automations", to: { name: "automations" }, icon: "bot", label: t("automations.sidebar.title") },
    { key: "quotes", to: { name: "quotes" }, icon: "receipt-euro", label: t("cms.priceCalculator.navTitle") },
    { key: "settings", to: { name: "settings" }, icon: "cog", label: t("cms.settings.title") },
  ]);

  const nextTheme = computed(() => {
    switch (theme.value) {
      case ThemeSetting.DARK:
        return ThemeSetting.LIGHT;
      case ThemeSetting.LIGHT:
        return ThemeSetting.DARK;
    }

    return ThemeSetting.DARK;
  });

  const themeSwitchLabel = computed(() => nextTheme.value === ThemeSetting.DARK ? t("base.switch_to_dark") : t("base.switch_to_light"));

  function switchTheme() {
    themeSetting.value = nextTheme.value;
  }

  function openDocs() {
    window.open("https://docs.verbleif.com", "_blank")?.focus();
  }

  return {
    t,
    version,
    items,
    switchTheme,
    themeSwitchLabel,
    openDocs,
  };
}
